import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { Box, Fab, FormControl, InputLabel, MenuItem, Select, Snackbar, makeStyles } from "@material-ui/core";
import { useEffect, useState } from "react";
import usePriceListApi, { PriceList } from "../../api/PriceListApi";
import PriceListDialog from './PriceListDialog';
import { DetailsMode } from '../DetailsDialog';
import { Alert, AlertTitle } from '@material-ui/lab';

interface Props {
  readonly defaultPriceListId?: number;
  readonly disabled?: boolean;
  readonly enableMutations?: boolean;
  onPriceListSelected: (priceListId: number) => void;
}
const useStyles = makeStyles(theme => ({
  button: {
    margin: theme.spacing(1)
  },
  select: {
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(1),
    width: 400,
  }
}));

const convertDateStringToComparableNumber = (dateAsString?: string) : number => {
  const date = dateAsString === undefined ? undefined : new Date(dateAsString);
  return convertDateToComparableNumber(date);
};

const convertDateToComparableNumber = (date?: Date) : number => {
  const timeInMilliseconds = date === undefined ? 0 : date.getTime();
  return isNaN(timeInMilliseconds) ? 0 : timeInMilliseconds;
};

const getDateTruncated = (dateAsString: string) => {
  const date = new Date(dateAsString);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

const isTodayBetweenPriceListStartAndEndDate = (priceList: PriceList) : boolean => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const startDate = priceList.startDate === undefined ? null : getDateTruncated(priceList.startDate);
  const endDate = priceList.endDate === undefined ? null : getDateTruncated(priceList.endDate);
  if (startDate === null || startDate <= today) {
    console.info(`start: ${startDate === null ? '?' : startDate} <= ${today} = true`);
    if (endDate === null || endDate >= today) {
      console.info(`end: ${endDate === null ? '?' : endDate} >= ${today} = true`);
      return true;
    } else {
      console.info(`end: ${endDate === null ? '?' : endDate} >= ${today} = false`);
      return false;
    }
  } else {
    console.info(`start: ${startDate === null ? '?' : startDate} <= ${today} = false`);
    return false;
  }
};

const PriceListSelection = (props: Props) => {
  const classes = useStyles();
  const priceListApi = usePriceListApi();
  const enableMutations = props.enableMutations === true;
  const [priceListDialogOpen, setPriceListDialogOpen] = useState<boolean>(false);
  const [priceListDialogMode, setPriceListDialogMode] = useState<DetailsMode|null>(null);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [selectedPriceList, setSelectedPriceList] = useState<PriceList|null>(null);
  const [deletedPriceList, setDeletedPriceList] = useState<PriceList|null>(null);
  const [errorMessage, setErrorMessage] = useState<string|null>(null);

  const handlePriceListSelectionChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const priceListId = event.target.value as number;
    const priceList = findPriceListById(priceListId);
    if (priceList !== undefined) {
      setSelectedPriceList(priceList);
      props.onPriceListSelected(priceListId);
    }
  };

  const findPriceListById = (priceListId: number) => priceLists.find(priceList => priceList.id === priceListId);

  const renderPriceListMenuItem = (priceList: PriceList) =>
    <MenuItem value={ priceList.id }>
        { priceList.name + ' (' }
        { priceList.startDate === undefined ? '' : ` od ${priceList.startDate}`}
        { priceList.endDate === undefined ? '' : ` do ${priceList.endDate}`}
        { ' )' }
    </MenuItem>;

  const initializePriceLists = () => priceListApi
    .fetchPriceLists()
    .then(priceLists => priceLists.sort((priceList, anotherPriceList) => priceList.id - anotherPriceList.id))
    .then(setPriceLists);

  const selectDefaultPriceList = () => {
    if (priceLists !== undefined && priceLists.length > 0) {
      console.info(`### Price lists: ${JSON.stringify(priceLists)}`);
      const defaultPriceList = determineDefaultPriceList();
      if (defaultPriceList !== undefined) {
        setSelectedPriceList(defaultPriceList);
        props.onPriceListSelected(defaultPriceList.id);
      }
    }
  };

  const findPriceListByCurentDate = () => {
    const priceListSortedByStartDate = [...priceLists]
      .sort((priceList, anotherPriceList) =>
        convertDateStringToComparableNumber(priceList.startDate) - convertDateStringToComparableNumber(anotherPriceList.startDate));
    return priceListSortedByStartDate.find(isTodayBetweenPriceListStartAndEndDate);
  };

  const determineDefaultPriceList = () => {
    const defaultPriceListId = props.defaultPriceListId;
    if (defaultPriceListId === undefined || defaultPriceListId === null) {
      return findPriceListByCurentDate();
    } else {
      return findPriceListById(defaultPriceListId);
    }
  };
  
  const handlePriceListAddClicked = () => {
    setPriceListDialogOpen(true);
    setPriceListDialogMode(DetailsMode.Create);
  };
  
  const handlePriceListEditClicked = () => {
    setPriceListDialogOpen(true);
    setPriceListDialogMode(DetailsMode.Update);
  };

  const handlePriceListDeleteClicked = () => {
    if (selectedPriceList !== null) {
      priceListApi
        .deletePriceList(selectedPriceList?.id)
        .then(setDeletedPriceList)
        .catch(error => setErrorMessage('Próba usunięcia cennika zańczyła się niepowodzeniem. Przed usunięciem upewnij się, że cennik jest pusty.'));
    }
  };

  const closePriceListDialog = () => {
    setPriceListDialogOpen(false);
    setPriceListDialogMode(null);
  };

  const clearErrorMessage = () => {
    setErrorMessage(null);
  };

  const getSelectValue = () => {
    const selectValue = selectedPriceList === undefined || selectedPriceList === null ? null : selectedPriceList.id;
    return selectValue === null ? '' : selectValue;
  };

  useEffect(() => {
    console.info(`priceListDialogOpen: ${priceListDialogOpen}`);
    if (priceListDialogOpen === false) {
      initializePriceLists();
    }
  }, [priceListDialogOpen, deletedPriceList]);

  useEffect(() => {
    selectDefaultPriceList();
  }, [priceLists, props.defaultPriceListId]);

  return <Box display="flex" flexDirection="row" justifyContent="flex-start">
    { priceLists && <FormControl
      className={ classes.select }
      variant='standard'
    >
      <InputLabel id="price-lists-selection-label" shrink={ true }>Wybierz cennik</InputLabel>
      <Select
          labelId='price-lists-selection-label'
          value={ getSelectValue() }
          id='price-list-selection'
          onChange={ handlePriceListSelectionChange }
          disabled={ props.disabled }
        >
          { priceLists.map(renderPriceListMenuItem) }
      </Select>
    </FormControl> }
    { enableMutations && <Fab
        aria-label='add'
        color='default'
        className={ classes.button }
        onClick={ handlePriceListAddClicked }
        size="medium"
    >
      <AddIcon />
    </Fab> }
    { enableMutations && <Fab
        aria-label='edit'
        color='primary'
        className={ classes.button }
        onClick={ handlePriceListEditClicked }
        size="medium"
    >
      <EditIcon />
    </Fab> }
    { enableMutations && <Fab
        aria-label='delete'
        color='secondary'
        className={ classes.button }
        onClick={ handlePriceListDeleteClicked }
        size="medium"
    >
      <DeleteIcon />
    </Fab> }
    { priceListDialogMode !== null && <PriceListDialog
        open={ priceListDialogOpen }
        mode={ priceListDialogMode }
        priceList={ selectedPriceList }
        onPriceListMutated={ closePriceListDialog }
        onCancel={ closePriceListDialog }
    /> }
    <Snackbar open={errorMessage !== null} onClose={ clearErrorMessage }>
      <Alert severity='error' onClose={ clearErrorMessage }>
        <AlertTitle>Uwaga</AlertTitle>
        <span>{ errorMessage }</span>
      </Alert>
    </Snackbar>
  </Box>
};

export default PriceListSelection;